import { useEffect, useState } from "react";
import { CollectsTable } from "@/components/collects/CollectsTable";
import { BaixarMtrsModal } from "@/components/collects/BaixarMtrsModal";
import { FiltersBar } from "@/components/shared/FiltersBar";
import { ApiErrorDisplay } from "@/components/shared/ApiErrorDisplay";
import { useCollectsApiData } from "@/hooks/useCollectsApiData";
import { useRecurringCollectsCSVExport } from "@/hooks/useRecurringCollectsCSVExport";
import { useUnifiedAuth } from "@/hooks/useUnifiedAuth";
import type { FilterState } from "@/hooks/useFilters";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { PERMISSIONS } from "@/constants/permissions";

const PAGE_SIZE = 20;

interface HistoricoColetasTabProps {
  filters: FilterState;
  onDateRangeChange: (dateRange: { start: Date; end: Date }) => void;
  onMaterialsChange: (materials: string[]) => void;
  onStatesChange: (states: string[]) => void;
  onEstablishmentsChange: (establishments: string[]) => void;
  onGeneratorsChange: (generators: string[]) => void;
  onHasMtrChange: (hasMtr: boolean | null) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export const HistoricoColetasTab = ({
  filters,
  onDateRangeChange,
  onMaterialsChange,
  onStatesChange,
  onEstablishmentsChange,
  onGeneratorsChange,
  onHasMtrChange,
  onClearFilters,
  hasActiveFilters,
}: HistoricoColetasTabProps) => {
  const { hasBaselinePermission } = usePermissions();
  const [page, setPage] = useState(1);
  const { isLoading: isProfileLoading } = useUnifiedAuth();
  const { handleExportCSV } = useRecurringCollectsCSVExport(filters);

  const {
    data: collectsResult,
    isLoading: isCollectsLoading,
    error,
    refetch,
  } = useCollectsApiData({ filters, page, pageSize: PAGE_SIZE, excludeStatus: ['planned'] });

  const currentPageData = collectsResult?.data ?? [];
  const pagination = collectsResult?.pagination;

  useEffect(() => {
    setPage(1);
  }, [
    filters?.dateRange?.start?.getTime(),
    filters?.dateRange?.end?.getTime(),
    filters?.materials?.join(","),
    filters?.states?.join(","),
    filters?.establishments?.join(","),
    filters?.generators?.join(","),
  ]);

  const isLoading =
    isCollectsLoading || (currentPageData.length === 0 && isProfileLoading);

  return (
    <div className="space-y-4 md:space-y-6">
      <FiltersBar
        analyticsPrefix="historico_coletas"
        filters={filters}
        onDateRangeChange={onDateRangeChange}
        onMaterialsChange={onMaterialsChange}
        onStatesChange={onStatesChange}
        onEstablishmentsChange={onEstablishmentsChange}
        onGeneratorsChange={onGeneratorsChange}
        onClearFilters={onClearFilters}
        hasActiveFilters={hasActiveFilters}
        // Sem o toggle Com/Sem MTR: a listagem do agreements não filtra por MTR (hasMtr responde
        // 400) e o mtr-manager só informa a situação por coleta, não oferece filtro equivalente.
        showHasMtrFilter={false}
        onHasMtrChange={onHasMtrChange}
      />

      {error ? (
        <ApiErrorDisplay error={error} onRetry={() => refetch()} context="coletas" />
      ) : (
        <CollectsTable
          data={currentPageData}
          isLoading={isLoading}
          onExportCSV={handleExportCSV}
          currentPage={pagination?.page ?? page}
          totalPages={pagination?.last_page ?? 0}
          totalItems={pagination?.count ?? 0}
          pageSize={pagination?.page_size ?? PAGE_SIZE}
          onPageChange={setPage}
          bulkDownloadSlot={
            hasBaselinePermission(
              PERMISSIONS.DOWNLOAD_COLLECTS_HISTORY_MTR_BULK,
            ) ? (
              <BaixarMtrsModal filters={filters} allowCdf />
            ) : null
          }
        />
      )}
    </div>
  );
};
